/**
 * Notification Manager
 * Provides a unified interface for web and native notifications using Capacitor.
 * Wraps @capacitor/local-notifications for cross-platform notification support.
 */

import { LocalNotifications } from '@capacitor/local-notifications';
import { Capacitor } from '@capacitor/core';
import { logger } from '../Analytics/Logger';

/**
 * Notification permission constants
 */
export const NotificationPermission = {
  GRANTED: 'granted',
  DENIED: 'denied',
  PROMPT: 'prompt'
} as const;

/**
 * Permission status for notifications
 */
export type NotificationPermissionStatus = typeof NotificationPermission[keyof typeof NotificationPermission];

/**
 * Platform information
 */
export interface PlatformInfo {
  platform: string;
  isNative: boolean;
  isWeb: boolean;
}

/**
 * Check current notification permission status
 */
export async function checkNotificationPermissionStatus(): Promise<NotificationPermissionStatus> {
  try {
    const result = await LocalNotifications.checkPermissions();
    return result.display as NotificationPermissionStatus;
  } catch (error) {
    logger.error('[NotificationManager] Error checking permissions:', error);
    return NotificationPermission.DENIED;
  }
}

/**
 * Request notification permissions from the user
 */
export async function requestNotificationPermission(): Promise<NotificationPermissionStatus> {
  try {
    logger.debug('[NotificationManager] Requesting notification permissions...');
    const result = await LocalNotifications.requestPermissions();
    const status = result.display as NotificationPermissionStatus;
    logger.debug('[NotificationManager] Permission result:', status);    
    return status;
  } catch (error) {
    logger.error('[NotificationManager] Error requesting permissions:', error);
    return NotificationPermission.DENIED;
  }
}

/**
 * Schedule a notification
 * @param title Notification title
 * @param body Notification body text
 * @param id Unique notification ID
 * @param delayMs Delay in milliseconds before showing notification (default: 100ms)
 */
export async function scheduleNotification(
  title: string,
  body: string,
  id: number = Date.now(),
  delayMs: number = 100
): Promise<void> {
  try {
    // Check if we have permission
    const permission = await checkNotificationPermissionStatus();
    if (permission !== NotificationPermission.GRANTED) {
      logger.warn('[NotificationManager] Cannot show notification - permission not granted');
      return;
    }

    // Schedule the notification
    await LocalNotifications.schedule({
      notifications: [{
        title,
        body,
        id,
        schedule: { at: new Date(Date.now() + delayMs) },
        sound: undefined,
        smallIcon: 'ic_stat_icon_config_sample',
        iconColor: '#6B8E23'
      }]
    });

    logger.debug(`[NotificationManager] Notification scheduled for ${delayMs}ms:`, title);
  } catch (error) {
    logger.error('[NotificationManager] Error scheduling notification:', error);
  }
}

/**
 * Get platform information
 */
export function getPlatformInfo(): PlatformInfo {
  const platform = Capacitor.getPlatform();
  const isNative = Capacitor.isNativePlatform();
  
  return {
    platform,
    isNative,
    isWeb: !isNative
  };
}
